/**
 * useBackgroundScheduler — runs periodic background tasks while the app is open.
 *
 * Manages two recurring tasks:
 * 1. SLA monitoring — every 15 minutes, checks for breached files and sends warnings
 * 2. Weekly reports — checks if it's time to send Monday/Friday reports
 *
 * Also handles persistent tracking of triggered calendar events to prevent
 * duplicate email prompts across page reloads.
 *
 * Only active for the adviser (Marinus), since he has the Graph API
 * permissions to send emails and read calendar.
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { runSLACheck } from '../api/slaMonitor';
import { sendMondayKickoff, sendFridayWrap, getScheduledReport } from '../api/weeklyReports';

const SLA_CHECK_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes
const REPORT_CHECK_INTERVAL_MS = 30 * 60 * 1000; // 30 minutes

// Track which reports have been sent today to avoid duplicates
const REPORTS_SENT_KEY = 'inbox_reports_sent';

function getReportsSentToday() {
  try {
    const data = localStorage.getItem(REPORTS_SENT_KEY);
    if (!data) return {};
    const parsed = JSON.parse(data);
    const today = new Date().toISOString().split('T')[0];
    // Reset if it's a new day
    if (parsed.date !== today) return {};
    return parsed;
  } catch {
    return {};
  }
}

function markReportSent(reportType) {
  const today = new Date().toISOString().split('T')[0];
  const current = getReportsSentToday();
  const updated = { ...current, date: today, [reportType]: true };
  localStorage.setItem(REPORTS_SENT_KEY, JSON.stringify(updated));
}

/**
 * @param {boolean} enabled — whether the scheduler should be active
 */
export default function useBackgroundScheduler(enabled = true) {
  const { role } = useAuth();
  const slaIntervalRef = useRef(null);
  const reportIntervalRef = useRef(null);

  // SLA check task
  const performSLACheck = useCallback(async () => {
    if (role !== 'adviser') return;

    try {
      const results = await runSLACheck();
      if (results.warned > 0) {
        console.log(`[Scheduler] SLA check: ${results.warned} new warning(s) sent out of ${results.breached} breached files`);
      }
    } catch (err) {
      console.warn('[Scheduler] SLA check failed:', err);
    }
  }, [role]);

  // Weekly report task
  const checkWeeklyReport = useCallback(async () => {
    if (role !== 'adviser') return;

    // Check if weekly reports are enabled in settings
    try {
      const settingsStr = localStorage.getItem('inbox_settings');
      if (settingsStr) {
        const settings = JSON.parse(settingsStr);
        if (settings.weeklyReportsEnabled === false) return;
      }
    } catch {
      // If settings can't be read, proceed with reports enabled
    }

    const reportType = getScheduledReport();
    if (!reportType) return;

    // Check if this report was already sent today
    const sentToday = getReportsSentToday();
    if (sentToday[reportType]) return;

    try {
      if (reportType === 'monday') {
        await sendMondayKickoff();
        console.log('[Scheduler] Monday kickoff report sent');
      } else if (reportType === 'friday') {
        await sendFridayWrap();
        console.log('[Scheduler] Friday wrap report sent');
      }
      markReportSent(reportType);
    } catch (err) {
      console.warn(`[Scheduler] Failed to send ${reportType} report:`, err);
    }
  }, [role]);

  useEffect(() => {
    if (!enabled || role !== 'adviser') return;

    // Run initial checks after a short delay (let the app settle)
    const initialTimeout = setTimeout(() => {
      performSLACheck();
      checkWeeklyReport();
    }, 10000); // 10-second delay

    // Set up recurring intervals
    slaIntervalRef.current = setInterval(performSLACheck, SLA_CHECK_INTERVAL_MS);
    reportIntervalRef.current = setInterval(checkWeeklyReport, REPORT_CHECK_INTERVAL_MS);

    return () => {
      clearTimeout(initialTimeout);
      if (slaIntervalRef.current) clearInterval(slaIntervalRef.current);
      if (reportIntervalRef.current) clearInterval(reportIntervalRef.current);
    };
  }, [enabled, role, performSLACheck, checkWeeklyReport]);

  return {
    triggerSLACheck: performSLACheck,
    triggerWeeklyReport: checkWeeklyReport,
  };
}
